import { useEffect, useMemo, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormHelperText, Typography } from '@mui/material'
import { LogbookPdfOptions } from 'types/graphql'
import * as yup from 'yup'

import { Form, useForm } from '@cedarjs/forms'
import { useMutation } from '@cedarjs/web'
import { toast } from 'react-hot-toast'

import useModal from 'src/hooks/useModal'
import fileDownload from 'src/utils/fileDownload'

import Modal from '../Modal/Modal'
import ModalActions from '../Modal/ModalActions'
import ModalContent from '../Modal/ModalContent'
import ModalHeader from '../Modal/ModalHeader'
import Button from '../MUI/Button'
import LoadingButton from '../MUI/LoadingButton'
import Checkbox from '../MUI/Checkbox'
import SelectDropdown from '../MUI/SelectDropdown'

import { CREATE_WORK_CARD_PDF, GET_WORK_CARD_USERS } from './queries'
import { useDispatch } from 'src/hooks/useDispatch'
import { addAsyncJob } from 'src/slices/asyncJobSlice'

type PrintWorkCardModalProps = {
  onBail?: () => void
  onCompleted?: (values: LogbookPdfOptions) => void
  discriminator?: string
  variety: 'due_list' | 'work_order' | 'compliance'
}

const modalName = 'printWorkCardModal'

const schema = yup.object().shape({
  renderCurrentTimesOnHeader: yup.boolean(),
  populateComplianceWithCurrentTimes: yup.boolean(),
  suppressNotes: yup.boolean(),
  includeReturnToServiceStatement: yup.boolean(),
  technicianId: yup
    .string()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  includeInspector: yup.boolean(),
  inspectorId: yup
    .string()
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  includeChildTasks: yup.boolean(),
  includeChildTaskCards: yup.boolean(),
  includeTaskCards: yup.boolean(),
  includeChildChecklist: yup.boolean(),
})

const PrintWorkCardModal: React.FC<PrintWorkCardModalProps> = ({
  onCompleted,
  onBail,
  discriminator,
  variety,
}) => {
  const defaultValues = {
    renderCurrentTimesOnHeader: variety !== 'due_list',
    populateComplianceWithCurrentTimes: false,
    suppressNotes: false,
    includeReturnToServiceStatement: false,
    technicianId: '',
    includeInspector: false,
    inspectorId: '',
    includeChildTasks: false,
    includeChildTaskCards: false,
    includeTaskCards: false,
    includeChildChecklist: false,
  }

  const {
    isOpen,
    handleClose,
    data: modalData,
  } = useModal(modalName, discriminator)
  const { ids } = modalData || { ids: [] }
  const dispatch = useDispatch()
  const formMethods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  })
  const [fetchUsers, { data }] = useLazyQuery(GET_WORK_CARD_USERS)
  const [createPdf, { loading: mutationLoading }] = useMutation(
    CREATE_WORK_CARD_PDF,
    {}
  )

  // Custom loading state to track both PDF generation and download
  const [isDownloading, setIsDownloading] = useState(false)
  // Combined loading state for UI
  const submitLoading = mutationLoading || isDownloading
  const formName = discriminator
    ? `printWorkCardModal-${discriminator}`
    : 'printWorkCardModal'

  const inspectorId = formMethods.watch('inspectorId')
  const includeInspector = formMethods.watch('includeInspector')
  const includeChildTasks = formMethods.watch('includeChildTasks')

  // on add children, ensure the checklist is also checked
  // on remove children, ensure the checklist and task cards are unchecked
  useEffect(() => {
    if (!includeChildTasks) {
      formMethods.setValue('includeChildTaskCards', false)
    }
  }, [includeChildTasks, formMethods])

  // Set the includeInspector checkbox based on the inspectorId value
  useEffect(() => {
    formMethods.setValue('includeInspector', !!inspectorId)
  }, [inspectorId, formMethods])

  // Set the inspectorId based on the includeInspector checkbox value
  useEffect(() => {
    if (!includeInspector) {
      formMethods.setValue('inspectorId', '')
    }
  }, [includeInspector, formMethods])

  useEffect(() => {
    // reset on the closure, so you don't see the label animation on the next open
    if (!isOpen) {
      formMethods.reset(defaultValues)
    } else {
      fetchUsers()
    }
  }, [isOpen, formMethods])

  const userOptions = useMemo(() => {
    const options =
      data?.users.map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id,
      })) || []

    return [{ label: <em>(None)</em>, value: '' }, ...options]
  }, [data?.users])

  const handleSubmit = async (values) => {
    // Start loading state
    setIsDownloading(true)

    const id = toast.loading('Generating PDF...')
    try {
      const result = await createPdf({
        variables: { ids, variety, options: values },
      })

      if (result.data?.createWorkCardPdf) {
        dispatch(
          addAsyncJob({
            id: result.data.createWorkCardPdf,
            status: 'pending',
            type: 'workCard',
            toastId: id,
          })
        )
        handleClose()
      } else {
        toast.error('Error generating PDF', { id })
      }
    } catch (_error) {
      toast.error('Error generating PDF', { id })
    } finally {
      // End loading state after everything is done
      onCompleted?.(values)
      setIsDownloading(false)
    }
  }

  if (!ids?.length) {
    return null
  }

  return (
    <Modal
      modalName={modalName}
      onBail={onBail}
      classes={{ paper: 'w-50' }}
      discriminator={discriminator}
    >
      <ModalHeader title="Print Work Cards" />
      <ModalContent>
        <Form id={formName} formMethods={formMethods} onSubmit={handleSubmit}>
          <div className="flex flex-col px-3">
            <div>
              <Checkbox
                name="renderCurrentTimesOnHeader"
                label="Print Current Times on Header"
                disabled={variety !== 'due_list'}
              />
              {variety !== 'due_list' && (
                <FormHelperText className="mt-0">
                  Applied times will be printed on the header
                </FormHelperText>
              )}
            </div>
            {/* <Checkbox
              name="includeReturnToServiceStatement"
              label="Include Return To Service Statement"
            /> */}
            <Checkbox name="suppressNotes" label="Suppress Notes" />
            <Checkbox name="includeTaskCards" label="Include Task Cards" />
            <Checkbox
              name="includeChildChecklist"
              label="Include Child Checklist"
            />
            <Checkbox name="includeChildTasks" label="Include Child Tasks" />
            <Checkbox
              className="ml-2"
              disabled={!includeChildTasks}
              name="includeChildTaskCards"
              label="Include Child Task Cards"
            />
          </div>
          <div className="mt-2 flex flex-col gap-2 px-3">
            <Typography variant="h6" className="mb-2">
              Signature Options
            </Typography>

            <div className="w-full">
              <SelectDropdown
                label="Technician"
                name="technicianId"
                options={userOptions}
                fullWidth
                disabled={variety !== 'due_list'}
              />
              {variety !== 'due_list' && (
                <FormHelperText>
                  Technician field prepopulated with task&apos;s Mechanic field
                </FormHelperText>
              )}
            </div>
            <Checkbox
              name="includeInspector"
              label="Include Inspector Signature"
            />
            <SelectDropdown
              label="Inspector"
              name="inspectorId"
              options={userOptions}
            />
          </div>
        </Form>
      </ModalContent>

      <ModalActions className="mt-3">
        <Button
          onClick={handleClose}
          variant="outlined"
          color="base"
          disabled={submitLoading}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={submitLoading}
          disabled={submitLoading}
          loadingPosition="start"
          fullWidth
          form={formName}
          type="submit"
          variant="contained"
          tooltip="Download work card PDF"
        >
          {submitLoading ? 'Generating PDF...' : 'Download PDF'}
        </LoadingButton>
      </ModalActions>
    </Modal>
  )
}

export default PrintWorkCardModal
